import { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';

import { isOfTypeUuId } from '@hiredigital/lib/helpers/utils';
import { PortfolioType } from '@hiredigital/lib/helpers/marketingEnum';
import { authRequest, request, reqNoIntercept } from '@apis/utils';

import IconDown from '@hiredigital/ui/Icon/icons/down.inline.svg';
import Button from '@hiredigital/ui/Button';
import PortfolioItem from './PortfolioItem';
import Packery from '@hiredigital/ui/Packery/NewPackery';
import PortfolioViewer from '@hiredigital/ui/PortfolioViewer/PortfolioViewer';

import {
  getPortfolioItemRequests,
  getLegacyPortfolioItemRequests,
  getPortfolioItemsRequests,
} from './apiMapper';

import Styles from './PortfolioList.module.scss';

// Because there is a need to handle item ids for fallback situations, we will need to include a case where the defaultActiveItemUuid is an id

const MAX_ITEMS = 100;
const MIN_ITEMS = 6;

export const Type = PortfolioType;

const PortfolioList = ({
  useContentWrapper,
  useScrollContainer,
  onLoad,
  creator = {},
  resourceUuid,
  type,
  data = [],
  onNavigate,
  // Custom props on marketing only
  isPrivate = false,
  externalSource = false,
  defaultActiveItemUuid,
  withLimit = false,
}) => {
  const [items, setItems] = useState(data);
  const [nextPage, setNextPage] = useState(null);
  const [nextPageLoading, setNextPageLoading] = useState(false);
  const [limit, setLimit] = useState(type === PortfolioType.PORTFOLIO ? MAX_ITEMS : MIN_ITEMS);

  const refContainer = useRef();
  const itemRef = useRef();

  let contentWrapper = null;
  let scrollerContainer = null;

  const openViewer = (item) => {
    itemRef.current.open(item);
  };

  const handleLimit = () => setLimit(limit === MAX_ITEMS ? MIN_ITEMS : MAX_ITEMS);

  const getVisibleItems = (items) => (withLimit ? items.slice(0, limit) : items);

  const visisbleItems = getVisibleItems(items);

  const memoPackeryOptions = useMemo(
    () => ({
      transitionDuration: '0.5s',
      gutter: `.${Styles.portfolioGutter}`,
      stamp: `.${Styles.portfolioStamp}`,
      gutterClass: Styles.portfolioGutter,
      originLeft: true,
      itemSelector: `.${Styles.portfolioItem}`,
    }),
    []
  );

  const portfolioItems = visisbleItems.map((item) => (
    <div key={item.id} id={item.id} className={Styles.portfolioItem}>
      <PortfolioItem
        handleClass={Styles.portfolioHandle}
        item={item}
        resourceUuid={resourceUuid}
        onPreview={openViewer}
      />
    </div>
  ));

  const loadItems = () => {
    getPortfolioItemsRequests[type](resourceUuid).then(
      ({ data }) => {
        setItems(data.results);
        setNextPage(data.meta.next);
      },
      (error) => {
        onLoad?.();
      }
    );
  };

  const onScroll = () => {
    const scrollDiff = Math.abs(contentWrapper.clientHeight - scrollerContainer.scrollTop);
    if (nextPage && !nextPageLoading && scrollDiff < 1600) {
      setNextPageLoading(true);
      const httpRequest = [
        PortfolioType.PUBLIC_PORTFOLIO,
        PortfolioType.PORTFOLIO,
        PortfolioType.RESUME,
        PortfolioType.PUBLIC_RESUME,
      ].includes(type)
        ? request
        : authRequest;
      httpRequest.get(nextPage).then(({ data }) => {
        if (!data.meta.next) {
          // console.log('no more page');
        } else {
          setNextPageLoading(false);
          setNextPage(data.meta.next);
        }
        setItems([...items, ...data.results]);
      });
    }
  };

  const previewItem = async (itemUuid) => {
    const isKey = isOfTypeUuId(itemUuid);
    let itemFromList, activeItem;
    if (isKey) {
      itemFromList = items.find((v) => v.uuid === itemUuid);
      activeItem = itemFromList || (await getLiveDefaultActiveItem(itemUuid));
    } else {
      // Fallback
      itemFromList = items.find((v) => v.id === itemUuid);
      activeItem = itemFromList || (await getLegacyDefaultActiveItem(itemUuid));
    }
    if (!itemFromList) {
      // previewed item might not exist in the initial list(since it loads the first page only)
      // so in order to navigate correctly we should add it to the end
      setItems([...items, activeItem]);
    }
    itemRef.current.open(activeItem);
  };

  const getLegacyDefaultActiveItem = async (itemUuid) => {
    // console.error('getLegacyDefaultActiveItem', type, resourceUuid, itemUuid);
    const { data } = await getLegacyPortfolioItemRequests[type](resourceUuid, itemUuid);
    return data;
  };

  const getLiveDefaultActiveItem = async (itemUuid) => {
    const { data } = await getPortfolioItemRequests[type](itemUuid);
    return data;
  };

  useEffect(() => {
    if (!externalSource) loadItems();
    if (defaultActiveItemUuid) previewItem(defaultActiveItemUuid);
  }, []);

  useEffect(() => {
    contentWrapper = refContainer?.current?.parentNode;
    if (typeof window !== 'undefined') {
      scrollerContainer = document.getElementById('scrollContainer');
    } else {
      scrollerContainer = contentWrapper?.parentNode;
    }
    scrollerContainer.addEventListener('scroll', onScroll);
    return () => scrollerContainer.removeEventListener('scroll', onScroll);
  }, [nextPage, nextPageLoading]); // Re-subscribing on each render - this would keep state dependencies updated everytime

  return (
    <Fragment>
      <div ref={refContainer} className={Styles.portfolioGrid}>
        {visisbleItems && (
          <>
            <Packery
              className={Styles.portfolioList}
              options={memoPackeryOptions}
              items={visisbleItems}
              allowDrag={false}>
              {portfolioItems}
            </Packery>
          </>
        )}
      </div>

      {withLimit && (
        <Fragment>
          {items.length > 6 && (
            <div className={Styles.portfolioAction}>
              <Button
                className={Styles.togglePortfolio}
                type={Button.Type.BLUE_OUTLINE}
                size={Button.Size.LARGE}
                onClick={handleLimit}>
                {limit === MIN_ITEMS && <span>{`See Full Portfolio`}</span>}
                {limit === MAX_ITEMS && (
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                    }}>
                    <IconDown className={Styles.upIcon} />
                    {`Show Less`}
                  </span>
                )}
              </Button>
            </div>
          )}
        </Fragment>
      )}

      <PortfolioViewer
        request={reqNoIntercept}
        isPrivate={isPrivate}
        items={items}
        ref={itemRef}
        type={type}
        getItem={getPortfolioItemRequests[type]}
        onNavigate={onNavigate}
      />
    </Fragment>
  );
};

PortfolioList.Type = PortfolioType;

PortfolioList.propTypes = {
  isPrivate: PropTypes.bool,
  onLoad: PropTypes.func,
  creator: PropTypes.object,
  resourceUuid: PropTypes.string,
  type: PropTypes.number,
};

export default PortfolioList;
